/**
 * Data-owning patient notifications page and document-read orchestration.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "@risalabs_frontend_org/oasis-ui-kit";
import { clinicalTrialsService } from "../api/clinicalTrialsService";
import { rowToPatient, toBackendDocType } from "../api/transformers";
import { useUnreadDocumentCounts } from "../hooks/useUnreadDocumentCounts";
import { PatientNotificationView } from "../views/PatientNotificationView";
import {
  DOCUMENT_TYPE_TABS,
  isVisibleDocumentTypeTab,
} from "../constants/documentTypes";
import {
  readStoredNotificationDate,
  writeStoredNotificationDate,
  yesterdayIso,
} from "../utils/notificationDate";
import {
  clearLegacyNotificationReadState,
  updateNotificationDocumentReadState,
} from "../utils/notificationReadCache";
import {
  getCachedNotificationPage,
  getCachedNotificationPatients,
  getNotificationScrollTop,
  getNotificationScrollContainer,
  getNotificationSort,
  NOTIFICATION_PAGE_SIZE,
  notificationQueryKey,
  notificationViewKey,
  setCachedNotificationPage,
  setCachedNotificationPatients,
  setNotificationScrollTop,
  setNotificationSort,
} from "../utils/notificationPageState";
import {
  buildNotificationFacetFilters,
  mapNotificationFacetOptions,
  normalizeNotificationFilters,
  notificationFiltersKey,
  type NotificationFacetOptions,
  type NotificationFilters,
} from "../utils/notificationFilters";
import {
  clearStoredNotificationFilters,
  readStoredNotificationFilters,
  writeStoredNotificationFilters,
} from "../utils/notificationFilterState";
import { toBackendNotificationSortBy } from "../utils/notificationSort";
import type { SortDirection } from "../components/custom-table/types";
import type { DocumentType, Patient } from "../types";

/** Default worklist sort applied when a view has no remembered sort. */
const DEFAULT_SORT_KEY = "nextAppointment";
const DEFAULT_SORT_DIRECTION: SortDirection = "asc";
/** Debounce free-text search before hitting the notifications API. */
const SEARCH_DEBOUNCE_MS = 300;

/**
 * Container for the Patient Notification View. Owns the roll-up date and the
 * active doc-type tab (both deep-linkable via `?date=` / `?tab=` from the
 * morning email CTA), fetches one page of notification rows from the API, maps
 * them into the view's `Patient` shape, and navigates to the Document Summary
 * View on row selection. Fetch failures surface as an oasis toast.
 *
 * The selected date is resolved once per mount with the precedence
 * `?date=` deep-link → session-stored date → yesterday, then persisted in
 * `sessionStorage` on explicit user changes so it sticks across in-app
 * navigation until logout or the 20h selection TTL (see
 * `../utils/notificationDate`).
 *
 * @returns The data-connected Patient Notification View
 */
export function NotificationsPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const dateFromUrl = searchParams.get("date");
  // Only honour ?tab= when it names a currently-visible tab (single source of
  // truth: DOCUMENT_TYPE_TABS in constants/documentTypes.ts). A hidden tab like
  // ECOG/Prescriptions or any bogus value falls back to the first visible tab, so we never
  // render no active tab or query an invalid doc type. Deriving the default from
  // the list keeps the two in lockstep even if the first visible tab changes.
  const tabParam = searchParams.get("tab");
  const initialTab: DocumentType = isVisibleDocumentTypeTab(tabParam)
    ? tabParam
    : DOCUMENT_TYPE_TABS[0];

  const [selectedDate, setSelectedDate] = useState<string>(
    () => dateFromUrl || readStoredNotificationDate() || yesterdayIso(),
  );
  const [activeDocumentType, setActiveDocumentType] =
    useState<DocumentType>(initialTab);
  const [filters, setFilters] = useState<NotificationFilters>(
    readStoredNotificationFilters,
  );
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const filterKey = useMemo(() => notificationFiltersKey(filters), [filters]);
  const viewKey = `${notificationViewKey(
    selectedDate,
    activeDocumentType,
  )}::${filterKey}`;
  const initialSort = getNotificationSort(viewKey);
  const [sortKey, setSortKey] = useState<string | null>(
    initialSort?.sortKey ?? DEFAULT_SORT_KEY,
  );
  const [sortDirection, setSortDirection] = useState<SortDirection>(
    initialSort?.direction ?? DEFAULT_SORT_DIRECTION,
  );
  const queryKey = notificationQueryKey(
    selectedDate,
    activeDocumentType,
    filterKey,
    {
      searchQuery: debouncedSearchQuery,
      sortKey,
      sortDirection,
      page,
    },
  );
  const cachedPage = getCachedNotificationPage(queryKey);
  const [patients, setPatients] = useState<Patient[]>(
    cachedPage?.patients ?? [],
  );
  const [totalCount, setTotalCount] = useState(cachedPage?.totalCount ?? 0);
  // Document-row range for the footer. Pagination counts patients, but the table
  // renders one row per document, so these come from the API rather than the page
  // arithmetic — and must be restored on a cache hit like every other count.
  const [totalDocumentCount, setTotalDocumentCount] = useState(
    cachedPage?.totalDocumentCount ?? 0,
  );
  const [documentOffset, setDocumentOffset] = useState(
    cachedPage?.documentOffset ?? 0,
  );
  const [facetOptions, setFacetOptions] = useState<NotificationFacetOptions>(
    {},
  );
  const [appointmentBounds, setAppointmentBounds] = useState<
    { min?: string; max?: string } | undefined
  >();
  const [areFacetsLoading, setAreFacetsLoading] = useState(true);
  const [facetError, setFacetError] = useState<string | null>(null);
  const [facetRetry, setFacetRetry] = useState(0);
  const {
    counts: unreadDocumentCounts,
    decrement: decrementUnreadCount,
    restore: restoreUnreadCount,
    revertRestore: revertUnreadCount,
    revalidate: revalidateUnreadCounts,
  } = useUnreadDocumentCounts(selectedDate);
  const [isLoading, setIsLoading] = useState<boolean>(!cachedPage);

  useEffect(() => {
    clearLegacyNotificationReadState();
  }, []);

  useEffect(() => {
    if (dateFromUrl) writeStoredNotificationDate(dateFromUrl);
  }, [dateFromUrl]);

  useEffect(() => {
    if (
      searchParams.get("date") === selectedDate &&
      searchParams.get("tab") === activeDocumentType
    ) {
      return;
    }
    setSearchParams(
      (previous) => {
        const next = new URLSearchParams(previous);
        next.set("date", selectedDate);
        next.set("tab", activeDocumentType);
        return next;
      },
      { replace: true },
    );
  }, [activeDocumentType, searchParams, selectedDate, setSearchParams]);

  useEffect(() => {
    const handle = window.setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
      setPage(0);
    }, SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(handle);
  }, [searchQuery]);

  const saveCurrentScroll = useCallback(() => {
    setNotificationScrollTop(
      viewKey,
      getNotificationScrollContainer()?.scrollTop ?? 0,
    );
  }, [viewKey]);

  const handleDateChange = useCallback(
    (date: string) => {
      saveCurrentScroll();
      setPage(0);
      setSelectedDate(date);
      writeStoredNotificationDate(date);
    },
    [saveCurrentScroll],
  );

  const handleDocumentTypeChange = useCallback(
    (documentType: DocumentType) => {
      saveCurrentScroll();
      setPage(0);
      setActiveDocumentType(documentType);
    },
    [saveCurrentScroll],
  );

  /**
   * Apply canonical filters and persist them for this browser-tab session.
   *
   * @param nextFilters - Filter values committed by the drawer or badges
   * @returns Nothing
   */
  const handleFiltersChange = useCallback(
    (nextFilters: NotificationFilters) => {
      saveCurrentScroll();
      setPage(0);
      const normalized = normalizeNotificationFilters(nextFilters);
      setFilters(normalized);
      writeStoredNotificationFilters(normalized);
    },
    [saveCurrentScroll],
  );

  /**
   * Clear all applied PRD filters without changing date, tab, or local search.
   *
   * @returns Nothing
   */
  const handleResetFilters = useCallback(() => {
    saveCurrentScroll();
    setPage(0);
    clearStoredNotificationFilters();
    setFilters({});
  }, [saveCurrentScroll]);

  /**
   * Update the free-text search box; the API query uses the debounced value.
   *
   * @param nextQuery - Raw search input
   * @returns Nothing
   */
  const handleSearchChange = useCallback(
    (nextQuery: string) => {
      saveCurrentScroll();
      setSearchQuery(nextQuery);
    },
    [saveCurrentScroll],
  );

  /**
   * Request another page of the current filtered result set.
   *
   * @param nextPage - 0-indexed page number
   * @returns Nothing
   */
  const handlePageChange = useCallback(
    (nextPage: number) => {
      saveCurrentScroll();
      setPage(Math.max(0, nextPage));
    },
    [saveCurrentScroll],
  );

  useEffect(() => {
    const controller = new AbortController();
    setAreFacetsLoading(true);
    setFacetError(null);
    clinicalTrialsService
      .getFacetCounts(
        selectedDate,
        toBackendDocType(activeDocumentType),
        controller.signal,
      )
      .then((response) => {
        setFacetOptions(mapNotificationFacetOptions(response.facet_results));
        setAppointmentBounds(response.range_results.next_appointment);
      })
      .catch((error: unknown) => {
        if (controller.signal.aborted) return;
        setFacetOptions({});
        setAppointmentBounds(undefined);
        setFacetError(
          error instanceof Error
            ? error.message
            : "Failed to load filter options",
        );
      })
      .finally(() => {
        if (!controller.signal.aborted) setAreFacetsLoading(false);
      });
    return () => controller.abort();
  }, [activeDocumentType, facetRetry, selectedDate]);

  useEffect(() => {
    const cached = getCachedNotificationPage(queryKey);
    if (cached) {
      // Restore every figure the view renders. This branch returns without
      // fetching, so anything left unset here keeps the previously loaded page's
      // value — which is how the footer range would drift on browser Back.
      setPatients(cached.patients);
      setTotalCount(cached.totalCount);
      setTotalPages(
        cached.totalCount === 0
          ? 0
          : Math.ceil(cached.totalCount / NOTIFICATION_PAGE_SIZE),
      );
      setTotalDocumentCount(cached.totalDocumentCount);
      setDocumentOffset(cached.documentOffset);
      setIsLoading(false);
      return undefined;
    }

    const controller = new AbortController();
    setIsLoading(true);
    const trimmedQuery = debouncedSearchQuery.trim();
    clinicalTrialsService
      .getNotifications(
        {
          date: selectedDate,
          docType: toBackendDocType(activeDocumentType),
          facetFilters: buildNotificationFacetFilters(filters),
          nextAppointmentFrom: filters.nextAppointment?.from,
          nextAppointmentTo: filters.nextAppointment?.to,
          query: trimmedQuery || undefined,
          sortBy: toBackendNotificationSortBy(sortKey),
          sortDir: sortDirection,
          page,
          hitsPerPage: NOTIFICATION_PAGE_SIZE,
        },
        controller.signal,
      )
      .then((res) => {
        const nextPatients = res.hits.map(rowToPatient);
        // An API deployed before the document counts omits both fields. Pass 0
        // through rather than substituting a patient count here: the view knows the
        // expanded row count and fills the gap in the right unit.
        const nextDocumentTotal = res.total_document_count ?? 0;
        const nextDocumentOffset = res.document_offset ?? 0;
        setCachedNotificationPage(queryKey, {
          patients: nextPatients,
          totalCount: res.total_count,
          totalDocumentCount: nextDocumentTotal,
          documentOffset: nextDocumentOffset,
        });
        setPatients(nextPatients);
        setTotalCount(res.total_count);
        setTotalPages(res.total_pages);
        setTotalDocumentCount(nextDocumentTotal);
        setDocumentOffset(nextDocumentOffset);
      })
      .catch((error: unknown) => {
        if (controller.signal.aborted) return;
        toast.error(
          error instanceof Error
            ? error.message
            : "Failed to load notifications",
          "Error",
        );
        setPatients([]);
        setTotalCount(0);
        setTotalPages(0);
        setTotalDocumentCount(0);
        setDocumentOffset(0);
      })
      .finally(() => {
        if (!controller.signal.aborted) setIsLoading(false);
      });
    return () => controller.abort();
  }, [
    activeDocumentType,
    debouncedSearchQuery,
    filterKey,
    filters,
    page,
    queryKey,
    selectedDate,
    sortDirection,
    sortKey,
  ]);

  useEffect(() => {
    if (isLoading) return undefined;
    const scrollTop = getNotificationScrollTop(viewKey);
    const frame = window.requestAnimationFrame(() => {
      const container = getNotificationScrollContainer();
      if (container) container.scrollTop = scrollTop;
    });
    return () => window.cancelAnimationFrame(frame);
  }, [isLoading, patients.length, viewKey]);

  useEffect(() => {
    const stored = getNotificationSort(viewKey);
    setSortKey(stored?.sortKey ?? DEFAULT_SORT_KEY);
    setSortDirection(stored?.direction ?? DEFAULT_SORT_DIRECTION);
  }, [viewKey]);

  /**
   * Apply a column sort and remember it for this view so browser Back restores
   * it (mirrors the scroll-restoration cache; in-memory only).
   *
   * @param nextSortKey - Clicked column key
   * @param direction - Resulting sort direction
   * @returns Nothing
   */
  const handleSortChange = useCallback(
    (nextSortKey: string, direction: SortDirection) => {
      setPage(0);
      setSortKey(nextSortKey);
      setSortDirection(direction);
      setNotificationSort(viewKey, { sortKey: nextSortKey, direction });
    },
    [viewKey],
  );

  const handlePatientSelect = useCallback(
    (patient: Patient, documentType: DocumentType) => {
      saveCurrentScroll();
      const openSummary = (selectedPatient: Patient) =>
        navigate(`/patients/${encodeURIComponent(patient.mrn)}/summary`, {
          state: {
            patient: selectedPatient,
            documentType,
            date: selectedDate,
            returnTo:
              `/notifications?date=${encodeURIComponent(selectedDate)}` +
              `&tab=${encodeURIComponent(activeDocumentType)}`,
          },
        });

      if (patient.isRead) {
        openSummary(patient);
        return;
      }
      if (!patient.documentId) {
        toast.error(
          "This notification is missing its document identifier.",
          "Unable to open notification",
        );
        return;
      }

      const backendDocumentType = toBackendDocType(documentType);
      const documentId = patient.documentId;
      const didAdjustUnreadCount = decrementUnreadCount(documentType);
      const optimisticPatients = updateNotificationDocumentReadState(
        patients,
        patient.mrn,
        backendDocumentType,
        documentId,
        true,
      );
      setPatients(optimisticPatients);
      setCachedNotificationPatients(queryKey, optimisticPatients);
      openSummary({ ...patient, isRead: true });

      clinicalTrialsService
        .markNotificationRead({
          date: selectedDate,
          document_type: backendDocumentType,
          mrn: patient.mrn,
          document_id: documentId,
        })
        .then(revalidateUnreadCounts)
        .catch((error: unknown) => {
          const currentPatients =
            getCachedNotificationPatients(queryKey) ?? optimisticPatients;
          const rolledBackPatients = updateNotificationDocumentReadState(
            currentPatients,
            patient.mrn,
            backendDocumentType,
            documentId,
            false,
          );
          setCachedNotificationPatients(queryKey, rolledBackPatients);
          setPatients(rolledBackPatients);
          if (didAdjustUnreadCount) restoreUnreadCount(documentType);
          toast.error(
            error instanceof Error
              ? error.message
              : "Failed to save notification read state",
            "Read state not saved",
          );
        });
    },
    [
      activeDocumentType,
      decrementUnreadCount,
      navigate,
      patients,
      restoreUnreadCount,
      revalidateUnreadCounts,
      queryKey,
      saveCurrentScroll,
      selectedDate,
    ],
  );

  const handleMarkUnread = useCallback(
    (patient: Patient, documentType: DocumentType) => {
      // Only a read row with a document id can be returned to unread.
      if (!patient.isRead || !patient.documentId) return;
      const backendDocumentType = toBackendDocType(documentType);
      const documentId = patient.documentId;
      // Optimistically bump the unread count (+1) and flip the row back to unread.
      restoreUnreadCount(documentType);
      const optimisticPatients = updateNotificationDocumentReadState(
        patients,
        patient.mrn,
        backendDocumentType,
        documentId,
        false,
      );
      setPatients(optimisticPatients);
      setCachedNotificationPatients(queryKey, optimisticPatients);

      clinicalTrialsService
        .markNotificationUnread({
          date: selectedDate,
          document_type: backendDocumentType,
          mrn: patient.mrn,
          document_id: documentId,
        })
        .then(revalidateUnreadCounts)
        .catch((error: unknown) => {
          const currentPatients =
            getCachedNotificationPatients(queryKey) ?? optimisticPatients;
          const rolledBackPatients = updateNotificationDocumentReadState(
            currentPatients,
            patient.mrn,
            backendDocumentType,
            documentId,
            true,
          );
          setCachedNotificationPatients(queryKey, rolledBackPatients);
          setPatients(rolledBackPatients);
          // Symmetric undo of the optimistic `restore` (+1): cache-based and unconditional,
          // so the badge is restored exactly even when the count was 0 before.
          revertUnreadCount(documentType);
          toast.error(
            error instanceof Error
              ? error.message
              : "Failed to update notification read state",
            "Unread state not saved",
          );
        });
    },
    [
      patients,
      restoreUnreadCount,
      revertUnreadCount,
      revalidateUnreadCounts,
      queryKey,
      selectedDate,
    ],
  );

  const handleMarkRead = useCallback(
    (patient: Patient, documentType: DocumentType) => {
      // Only an unread row with a document id can be marked read in place.
      if (patient.isRead || !patient.documentId) return;
      const backendDocumentType = toBackendDocType(documentType);
      const documentId = patient.documentId;
      // Optimistically drop the unread count (-1) and flip the row to read.
      const didAdjustUnreadCount = decrementUnreadCount(documentType);
      const optimisticPatients = updateNotificationDocumentReadState(
        patients,
        patient.mrn,
        backendDocumentType,
        documentId,
        true,
      );
      setPatients(optimisticPatients);
      setCachedNotificationPatients(queryKey, optimisticPatients);

      clinicalTrialsService
        .markNotificationRead({
          date: selectedDate,
          document_type: backendDocumentType,
          mrn: patient.mrn,
          document_id: documentId,
        })
        .then(revalidateUnreadCounts)
        .catch((error: unknown) => {
          const currentPatients =
            getCachedNotificationPatients(queryKey) ?? optimisticPatients;
          const rolledBackPatients = updateNotificationDocumentReadState(
            currentPatients,
            patient.mrn,
            backendDocumentType,
            documentId,
            false,
          );
          setCachedNotificationPatients(queryKey, rolledBackPatients);
          setPatients(rolledBackPatients);
          if (didAdjustUnreadCount) restoreUnreadCount(documentType);
          toast.error(
            error instanceof Error
              ? error.message
              : "Failed to save notification read state",
            "Read state not saved",
          );
        });
    },
    [
      patients,
      decrementUnreadCount,
      restoreUnreadCount,
      revalidateUnreadCounts,
      queryKey,
      selectedDate,
    ],
  );

  const handleOpenNotes = useCallback(
    (patient: Patient, documentType: DocumentType) => {
      // Navigation-only: dock the notes panel via query param without mutating
      // the document's read/unread state.
      saveCurrentScroll();
      navigate(
        `/patients/${encodeURIComponent(patient.mrn)}/summary?notes=open`,
        {
          state: {
            patient,
            documentType,
            date: selectedDate,
            returnTo:
              `/notifications?date=${encodeURIComponent(selectedDate)}` +
              `&tab=${encodeURIComponent(activeDocumentType)}`,
          },
        },
      );
    },
    [navigate, saveCurrentScroll, selectedDate, activeDocumentType],
  );

  return (
    <PatientNotificationView
      onPatientSelect={handlePatientSelect}
      onMarkUnread={handleMarkUnread}
      onMarkRead={handleMarkRead}
      onOpenNotes={handleOpenNotes}
      activeDocumentType={activeDocumentType}
      onDocumentTypeChange={handleDocumentTypeChange}
      onViewChange={(view) => {
        if (view === "notes") navigate("/notes");
      }}
      patients={patients}
      unreadDocumentCounts={unreadDocumentCounts}
      selectedDate={selectedDate}
      onDateChange={handleDateChange}
      isLoading={isLoading}
      filters={filters}
      onFiltersChange={handleFiltersChange}
      onResetFilters={handleResetFilters}
      facetOptions={facetOptions}
      appointmentBounds={appointmentBounds}
      areFacetsLoading={areFacetsLoading}
      facetError={facetError}
      onRetryFacets={() => setFacetRetry((attempt) => attempt + 1)}
      searchQuery={searchQuery}
      onSearchChange={handleSearchChange}
      totalCount={totalCount}
      totalDocumentCount={totalDocumentCount}
      documentOffset={documentOffset}
      page={page}
      totalPages={totalPages}
      onPageChange={handlePageChange}
      sortKey={sortKey}
      sortDirection={sortDirection}
      onSortChange={handleSortChange}
      onTagsUpdated={() => setFacetRetry((attempt) => attempt + 1)}
    />
  );
}
